'use client'

// Shared sign-in form used by /sign/in and /sign/oauth/in. After a successful
// login it calls router.refresh(): on /sign/in the middleware bounces the
// signed-in visitor to their dashboard, and on /sign/oauth/in the server
// page re-runs, sees the new session and swaps this form for the consent
// screen. No client-side redirect knowledge needed in either case.

import { zodResolver } from '@hookform/resolvers/zod'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import LoadingButton from '@mui/lab/LoadingButton'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useAnalytics } from '~/app/_hooks/use_analytics'
import { useChallenge } from '~/app/sign/_hooks/use_challenge'
import client from '~/lib/client/client'
import CodeError from '~/lib/client/error'

export default function SignInForm() {
  const t = useTranslations('auth.sign_in')
  const analytics = useAnalytics()
  const { challenge } = useChallenge()
  const router = useRouter()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [ready, setReady] = useState(false)

  // Until React takes over the submit, an Enter key would send the fields
  // to the server as a GET query string.
  useEffect(() => setReady(true), [])

  const schema = z.object({
    email: z.string().email({ message: t('form.email.error') }),
    password: z.string().min(6, { message: t('form.password.error') }),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: any) => {
    setLoading(true)

    const pow = await challenge()

    const [_, err] = await client({
      path: '/api/auth/in',
      method: 'POST',
      body: {
        email: data.email,
        password: data.password,
        ...pow,
      },
    })

    setLoading(false)

    if (err?.code == CodeError.code.Unauthenticated) {
      setError(t('form.errors.invalid_credentials'))
      return
    }

    if (err) {
      setError(err.say || t('form.errors.default'))
      return
    }

    analytics.track('login', {
      email: data.email,
    })

    router.refresh()
  }

  return (
    <Box sx={{ width: '100%' }}>
      <form method='post' onSubmit={handleSubmit(onSubmit)}>
        {/* Email Input */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant='body2'
            sx={{ color: '#f0f0f1', fontWeight: 500, mb: 1.5 }}
          >
            Email
          </Typography>
          <TextField
            id='email'
            placeholder='Enter your email'
            fullWidth
            {...register('email')}
            error={!!errors.email}
            helperText={errors.email?.message?.toString() || ''}
            autoComplete='email'
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#1C1C1E',
                borderRadius: '12px',
                '& fieldset': {
                  borderColor: '#2a2a2a',
                },
                '&:hover fieldset': {
                  borderColor: '#3a3a3a',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#27B1C6',
                },
              },
              '& .MuiInputBase-input': {
                color: '#f0f0f1',
                padding: '16px',
                '&::placeholder': {
                  color: '#8E8E93',
                  opacity: 1,
                },
              },
            }}
          />
        </Box>

        {/* Password Input */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant='body2'
            sx={{ color: '#f0f0f1', fontWeight: 500, mb: 1.5 }}
          >
            Password
          </Typography>
          <TextField
            id='password'
            placeholder='Enter your password'
            type={showPassword ? 'text' : 'password'}
            fullWidth
            {...register('password')}
            error={!!errors.password}
            helperText={errors.password?.message?.toString() || ''}
            autoComplete='current-password'
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge='end'
                    disabled={loading}
                    sx={{ color: '#8E8E93' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#1C1C1E',
                borderRadius: '12px',
                '& fieldset': {
                  borderColor: '#2a2a2a',
                },
                '&:hover fieldset': {
                  borderColor: '#3a3a3a',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#27B1C6',
                },
              },
              '& .MuiInputBase-input': {
                color: '#f0f0f1',
                padding: '16px',
                '&::placeholder': {
                  color: '#8E8E93',
                  opacity: 1,
                },
              },
            }}
          />
        </Box>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Forgot Password Link */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Link
            href='/sign/email?reason=password_reset'
            sx={{
              color: '#27B1C6',
              fontSize: '14px',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Forgot Password?
          </Link>
        </Box>

        {/* Sign In Button */}
        <LoadingButton
          variant='contained'
          type='submit'
          fullWidth
          loading={loading}
          disabled={!ready}
          sx={{
            py: 1,
            borderRadius: '12px',
            background: loading
              ? '#1a1a1a'
              : 'linear-gradient(135deg, #27B1C6 0%, #59CDA1 100%)',
            boxShadow: loading ? 'none' : '0 2px 8px rgba(39, 177, 198, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2ed3ed 0%, #6df6c1 100%)',
              boxShadow: '0 4px 12px rgba(39, 177, 198, 0.4)',
            },
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'none',
          }}
        >
          Sign In
        </LoadingButton>

        {/* Sign Up Link */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 0.5,
            mt: 4,
          }}
        >
          <Typography variant='body2' sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Not registered yet?
          </Typography>
          <Link
            href='/sign/up'
            sx={{
              color: '#27B1C6',
              fontSize: '14px',
              fontWeight: 600,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Sign Up
          </Link>
        </Box>
      </form>
    </Box>
  )
}
